import InitApi from "api/InitApi";
import { useEffect, useRef, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import useTokenStore from "store/tokenStore";
import { deleteEmptyObjects, fetchConfig } from "utils";
import {
	ECOCASH_POLL_INTERVAL,
	ECOCASH_MAX_POLL_DURATION,
} from "utils/paymentConfig";

const useCheckEcocashStatus = (queryParams, shouldFetch) => {
	const { accessToken } = useTokenStore();
	const queryClient = useQueryClient();
	const location = useLocation();
	const history = useHistory();

	// Track polling start time and timeout state
	const pollingStartTime = useRef(null);
	const [isTimedOut, setIsTimedOut] = useState(false);
	const [elapsedTime, setElapsedTime] = useState(0);

	// Initialize polling start time when shouldFetch becomes true
	useEffect(() => {
		if (shouldFetch && !pollingStartTime.current) {
			pollingStartTime.current = Date.now();
			setIsTimedOut(false);
			setElapsedTime(0);
		} else if (!shouldFetch) {
			pollingStartTime.current = null;
		}
	}, [shouldFetch]);

	// Update elapsed time every second while polling
	useEffect(() => {
		if (!shouldFetch || isTimedOut) return;

		const interval = setInterval(() => {
			if (pollingStartTime.current) {
				const elapsed = Date.now() - pollingStartTime.current;
				setElapsedTime(elapsed);

				// Check if we've exceeded the max polling duration
				if (elapsed >= ECOCASH_MAX_POLL_DURATION) {
					setIsTimedOut(true);
				}
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [shouldFetch, isTimedOut]);

	const fetch = async () => {
		const instance = await InitApi({ accessToken });

		const params = queryParams ? deleteEmptyObjects(queryParams) : null;
		const response = await instance.get("/orders/ecocash-status", {
			params,
		});

		const res = response?.data;
		const payload = res?.data ?? res;

		if (payload?.terminal) {
			const nextStatus =
				payload?.status === "Completed" || payload?.status === "Paid"
					? "Successful"
					: payload?.status === "Failed"
					? "Failed"
					: null;

			if (nextStatus) {
				const searchParams = new URLSearchParams(location.search);
				searchParams.set("status", nextStatus);
				history.push(`${location.pathname}?${searchParams.toString()}`);
			}
		}

		queryClient.invalidateQueries("user_cart_items");
		queryClient.invalidateQueries("user_cart_count");
		queryClient.invalidateQueries("is_subscribed");
		queryClient.invalidateQueries("picks");
		queryClient.invalidateQueries("purchased");

		return res;
	};

	// Reset timeout state (useful for retry functionality)
	const resetTimeout = useCallback(() => {
		pollingStartTime.current = Date.now();
		setIsTimedOut(false);
		setElapsedTime(0);
	}, []);

	// Determine if we should continue polling
	const shouldContinuePolling = shouldFetch && !isTimedOut;

	const queryResult = useQuery("payment-response", () => fetch(), {
		...fetchConfig,
		retry: 1,
		staleTime: 0,
		refetchInterval: shouldContinuePolling ? ECOCASH_POLL_INTERVAL : false,
		enabled: shouldContinuePolling,
	});

	return {
		...queryResult,
		isTimedOut,
		elapsedTime,
		resetTimeout,
		maxDuration: ECOCASH_MAX_POLL_DURATION,
	};
};

export default useCheckEcocashStatus;
